import React from 'react';
import { useSmartProp } from '@smartfire/gutenberg-react';
import {
    PanelBody,
    PanelRow,
    TextControl,
    ToggleControl,
    SelectControl,
    Button as WPButton,
    ButtonGroup as WPButtonGroup,
} from '@wordpress/components';
import { MediaUpload, MediaUploadCheck } from '@wordpress/block-editor';
import { useSelect } from '@wordpress/data';
import he from 'he';

export const Inspector = () => {
    const [text, setText] = useSmartProp('text');
    const [linkType, setLinkType] = useSmartProp('linkType');
    const [link, setLink] = useSmartProp('link');
    const [postType, setPostType] = useSmartProp('postType');
    const [social, setSocial] = useSmartProp('social');
    const [fileId, setFileId] = useSmartProp('fileId');
    const [targetBlank, setTargetBlank] = useSmartProp('targetBlank');
    const [type, setType] = useSmartProp('type');
    const [align, setAlign] = useSmartProp('align');
    const [withWrapper, setWrapper] = useSmartProp('withWrapper');

    const onSelectFile = (file) => {
        setFileId(file.id);
        setLink(file.url);
    };
    
    const getUrlAndTitle = (el) => {
        return {
            value: el.link,
            label: he.decode(el.title.rendered),
        };
    };

    const getSlugAndName = (el) => {
        return {
            value: el.slug,
            label: he.decode(el.labels.name),
        };
    };

    let { file, pages, postTypes, posts } = useSelect((select) => {
        return {
            file: fileId ? select('core').getMedia(fileId) : undefined,
            pages: select('core').getEntityRecords('postType', 'page', { per_page: -1 }),
            postTypes: select('core').getPostTypes(),
            posts: postType ? select('core').getEntityRecords('postType', postType, { per_page: -1 }) : [],
        };
    });

    if (pages && Array.isArray(pages)) {
        pages = pages.map(getUrlAndTitle);
    }

    if (posts && Array.isArray(posts)) {
        posts = posts.map(getUrlAndTitle);
    }

    if (postTypes && Array.isArray(postTypes)) {
        postTypes = postTypes.filter((el) => el.viewable).map(getSlugAndName);
    }

    const socials = [
        { label: 'Facebook', value: 'facebook' },
        { label: 'Twitter', value: 'twitter' },
        { label: 'LinkedIn', value: 'linkedin' },
        { label: 'Instagram', value: 'instagram' },
        { label: 'Youtube', value: 'youtube' },
    ];

    return (
        <>
            <PanelBody title="Contenu" initialOpen={true}>
                <PanelRow>
                    <TextControl label="Texte" value={text} onChange={setText} />
                </PanelRow>
            </PanelBody>
            <PanelBody title="Lien" initialOpen={true}>
                <PanelRow>
                    <SelectControl
                        label="Type de lien"
                        value={linkType}
                        onChange={(value) => {
                            setLinkType(value);
                            setLink('');
                            setFileId(0);
                        }}
                        options={[
                            { value: 'page', label: 'Page' },
                            { value: 'post', label: 'Post' },
                            { value: 'file', label: 'Fichier' },
                            { value: 'email', label: 'Email' },
                            { value: 'tel', label: 'Téléphone' },
                            { value: 'lienext', label: 'Lien externe' },
                            { value: 'social', label: 'Réseau social' },
                            { value: 'other', label: 'Autre' },
                        ]}
                        style={{ width: '100%' }}
                    />
                </PanelRow>
                <PanelRow>
                    {
                        {
                            page: <SelectControl label="Page" value={link} onChange={setLink} options={pages} />,
                            post: (
                                <SelectControl
                                    label="Post Type"
                                    value={postType}
                                    onChange={setPostType}
                                    options={postTypes}
                                />
                            ),
                            file: (
                                <MediaUploadCheck>
                                    <MediaUpload
                                        onSelect={onSelectFile}
                                        allowedTypes={['application']}
                                        value={fileId}
                                        render={({ open }) => (
                                            <WPButton
                                                className={'editor-post-featured-image__toggle'}
                                                onClick={open}
                                                isDefault
                                            >
                                                {fileId && file ? (
                                                    <>
                                                        {file.title.rendered}
                                                        <br />
                                                        Remplacer le fichier
                                                    </>
                                                ) : (
                                                    'Choisir un fichier'
                                                )}
                                            </WPButton>
                                        )}
                                    />
                                </MediaUploadCheck>
                            ),
                            email: <TextControl label="Email" type="email" value={link} onChange={setLink} />,
                            tel: <TextControl label="Téléphone" type="tel" value={link} onChange={setLink} />,
                            lienext: (
                                <TextControl label="Lien externe" type="lienext" value={link} onChange={setLink} />
                            ),
                            social: (
                                <SelectControl
                                    label="Réseau Social"
                                    value={social}
                                    onChange={setSocial}
                                    options={socials}
                                />
                            ),
                            other: <TextControl label="Lien" value={link} onChange={setLink} />,
                        }[linkType]
                    }
                </PanelRow>
                {linkType === 'social' && (
                    <PanelRow>
                        <TextControl label="Lien" value={link} onChange={setLink} />
                    </PanelRow>
                )}
                {postType && posts && (
                    <PanelRow>
                        <SelectControl label="Post" value={link} onChange={setLink} options={posts} />
                    </PanelRow>
                )}
                <PanelRow>
                    <ToggleControl
                        label="Ouvrir dans un nouvel onglet"
                        checked={targetBlank}
                        onChange={setTargetBlank}
                    />
                </PanelRow>
            </PanelBody>
            <PanelBody title="Style" initialOpen={true}>
                <PanelRow>
                    <SelectControl
                        label="Type"
                        value={type}
                        onChange={setType}
                        options={[
                            { value: 'primary', label: 'Primaire' },
                            { value: 'primaryOutlined', label: 'Primaire vide' },
                            { value: 'secondary', label: 'Secondaire' },
                            { value: 'secondaryOutlined', label: 'Secondaire vide' },
                            { value: 'third', label: 'Tertiaire' },
                            { value: 'thirdOutlined', label: 'Tertiaire vide' },
                            { value: 'basicOutlined', label: 'Basique vide' },
                        ]}
                    />
                </PanelRow>
                <PanelRow>Alignement</PanelRow>
                <PanelRow>
                    <WPButtonGroup label="Alignement">
                        <WPButton isPrimary={align === 'left'} onClick={() => setAlign('left')}>
                            Gauche
                        </WPButton>
                        <WPButton isPrimary={align === 'center'} onClick={() => setAlign('center')}>
                            Centré
                        </WPButton>
                        <WPButton isPrimary={align === 'right'} onClick={() => setAlign('right')}>
                            Droite
                        </WPButton>
                    </WPButtonGroup>
                </PanelRow>
                <PanelRow>
                    <ToggleControl
                        label="Ajouter un container ?" 
                        checked={withWrapper} 
                        onChange={setWrapper} />
                </PanelRow>
            </PanelBody>
        </>
    );
}

export default Inspector;
